import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../../constants/config";
import "./CometChat.css";

const getCometChat = () =>
  window.CometChat || window.CometChatApp?.CometChat;

const isCometChatEmbedReady = () => {
  const CometChat = getCometChat();

  return Boolean(
    window.CometChatApp?.CometChatAuth?.start ||
    (CometChat && typeof CometChat.init === "function")
  );
};

const waitForCometChatEmbedReady = (resolve, reject, attempt = 0) => {
  if (isCometChatEmbedReady()) {
    resolve();
    return;
  }

  if (attempt >= 30) {
    reject(new Error("CometChat embed SDK did not become available"));
    return;
  }

  window.setTimeout(() => waitForCometChatEmbedReady(resolve, reject, attempt + 1), 100);
};


const getCometChatStorageScope = (appId, uid) => `${appId || "noapp"}:${uid || "nouid"}`;

const getTenantCookieScope = () => {
  try {
    const fromStorage = localStorage.getItem("tenant");
    if (fromStorage) return fromStorage;
    const match = document.cookie.match(/(?:^|;\s*)tenant=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : "unknown-tenant";
  } catch (_err) {
    return "unknown-tenant";
  }
};


const normalizeCometChatRegion = (value) => String(value || "us").trim().toLowerCase();

const buildCometChatAppSettings = (region) => {
  const CometChat = getCometChat();
  if (!CometChat?.AppSettingsBuilder) {
    return null;
  }
  const builder = new CometChat.AppSettingsBuilder()
    .setRegion(normalizeCometChatRegion(region))
    .subscribePresenceForAllUsers()
    .autoEstablishSocketConnection(true);
  if (CometChat.StorageMode?.SESSION && typeof builder.setStorageMode === "function") {
    builder.setStorageMode(CometChat.StorageMode.SESSION);
  }
  return builder.build();
};

const initCometChatSdk = async (appId, region) => {
  const CometChat = getCometChat();
  if (!CometChat?.init) {
    return;
  }
  const appSettings = buildCometChatAppSettings(region);
  if (appSettings) {
    await CometChat.init(appId, appSettings);
    return;
  }
  await CometChat.init(appId, normalizeCometChatRegion(region));
};

const ensureCometChatEmbedScript = () => {
  if (isCometChatEmbedReady()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let script = document.querySelector(
      'script[src*="@cometchat/chat-embed"]'
    );

    if (!script) {
      script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/@cometchat/chat-embed@1.0.35/dist/main.js";
      script.async = true;

      script.onload = () => {
        waitForCometChatEmbedReady(resolve, reject);
      };

      script.onerror = () => {
        reject(new Error("Failed to load CometChat Embed"));
      };

      document.head.appendChild(script);
      return;
    }

    script.addEventListener("load", () =>
      waitForCometChatEmbedReady(resolve, reject)
    );

    script.addEventListener("error", () =>
      reject(new Error("Failed to load CometChat Embed"))
    );

    waitForCometChatEmbedReady(resolve, reject);
  });
};

const CometChatEmbed = ({ user }) => {
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const [hasCometChatAccess, setHasCometChatAccess] = useState(false);
  const messageListenerIdRef = useRef(`kapp-cometchat-web-notify-${Date.now()}`);
  const callListenerIdRef = useRef(`kapp-cometchat-call-${Date.now()}`);
  const callListenerRetryTimerRef = useRef(null);
  const activeCallSessionIdRef = useRef("");
  const callMaxDurationSecRef = useRef(0);
  const callMaxDurationTimerRef = useRef(null);
  const [isInCall, setIsInCall] = useState(false);
  const listenerRetryTimerRef = useRef(null);
  const lastIframeNotificationAtRef = useRef(0);
  const iframePollTimerRef = useRef(null);
  const iframeLastSnapshotRef = useRef(null);
  const injectTimeoutIdsRef = useRef([]);
  const shownNotificationKeysRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const activeNotificationsRef = useRef(new Set());
  const widgetStartConfigRef = useRef(null);
  const widgetUidRef = useRef("");
  const cometChatSessionRef = useRef({ appId: "", uid: "" });
  const loggedInCometUidRef = useRef("");
  const unreadCountRef = useRef(0);
  const readReceiptOriginalFnsRef = useRef({});
  const pendingReadReceiptCallsRef = useRef([]);
  const pendingFocusRef = useRef(null);
  const widgetStartPromiseRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [toasts, setToasts] = useState([]);
  const [retryKey, setRetryKey] = useState(0);
  const initGenerationRef = useRef(0);
  const effectiveUser = user;
  const storageScopeRef = useRef(`${getTenantCookieScope()}:${effectiveUser?.entity?.id || "anon"}`);
  const notificationStoreKeyRef = useRef(`kapp-cometchat-shown-notifications-${storageScopeRef.current}`);
  const unreadStoreKeyRef = useRef(`kapp-cometchat-unread-count-${storageScopeRef.current}`);

  useEffect(() => {
    isMountedRef.current = true;
    try {
      const storedUnread = Number(sessionStorage.getItem(unreadStoreKeyRef.current) || 0);
      const safeUnread = Number.isFinite(storedUnread) && storedUnread > 0 ? Math.floor(storedUnread) : 0;
      window.__kappCometChatUnreadCount = safeUnread;
      unreadCountRef.current = safeUnread;
      window.dispatchEvent(new CustomEvent("kapp:cometchat-unread", { detail: { count: safeUnread } }));
    } catch (_err) {
      window.__kappCometChatUnreadCount = 0;
    }
    return () => {
      isMountedRef.current = false;
      activeNotificationsRef.current.forEach((item) => {
        try {
          item.close();
        } catch (_err) {}
      });
      activeNotificationsRef.current.clear();
    };
  }, []);

  const publishCometChatUnreadCount = (count) => {
    const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    unreadCountRef.current = safeCount;
    window.__kappCometChatUnreadCount = safeCount;
    try {
      sessionStorage.setItem(unreadStoreKeyRef.current, String(safeCount));
    } catch (_err) {}
    window.dispatchEvent(new CustomEvent("kapp:cometchat-unread", { detail: { count: safeCount } }));
  };

  const incrementCometChatUnreadCount = () => {
    publishCometChatUnreadCount(unreadCountRef.current + 1);
  };

  const clearCometChatUnreadCount = () => {
    publishCometChatUnreadCount(0);
  };

  const playChatSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") void ctx.resume();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.12);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.07, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    } catch (_err) {}
  };

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  };

  const pushInAppToast = (toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev.slice(-2), { ...toast, id }]);
    playChatSound();
    window.setTimeout(() => dismissToast(id), 7000);
  };

  const persistShownNotificationKeys = () => {
    try {
      const values = Array.from(shownNotificationKeysRef.current).slice(-150);
      sessionStorage.setItem(notificationStoreKeyRef.current, JSON.stringify(values));
    } catch (_err) {}
  };

  const requestNotificationPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return;
    }

    if (!window.isSecureContext) {
      console.warn("Browser notifications require HTTPS or localhost.");
      return;
    }

    if (window.Notification.permission === "default") {
      try {
        await window.Notification.requestPermission();
      } catch (err) {
        console.warn("Notification permission request failed:", err);
      }
    }
  };

  const normalizeNotificationBody = (value) => {
    const cleaned = String(value || "")
      .replace(/\s+\d{1,2}:\d{2}(?:\s?[AP]M)?$/i, "")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned || "You received a new message";
  };

  const extractMessageText = (message) => {
    const candidates = [
      message?.text,
      message?.data?.text,
      message?.data?.message,
      message?.data?.customData?.text,
      message?.data?.customData?.message,
      message?.data?.metadata?.text,
      message?.data?.metadata?.message,
    ];

    const firstText = candidates.find((item) => typeof item === "string" && item.trim());
    return firstText ? String(firstText) : "";
  };

  const extractSenderName = (message) => {
    const raw = message?.sender?.name
      || message?.data?.sender?.name
      || message?.title
      || message?.sender?.uid
      || message?.data?.sender?.uid
      || "New message";
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      return trimmed || "New message";
    }
    return "New message";
  };

  const getToastHeadline = (toast) => {
    const headline = toast?.senderName || toast?.title;
    if (typeof headline === "string" && headline.trim()) {
      return headline.trim();
    }
    return "New message";
  };

  const normalizeIdentityValue = (value) => {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  };

  const getLoggedInCometUids = (currentUid) => {
    const uids = new Set();
    [
      currentUid,
      widgetUidRef.current,
      loggedInCometUidRef.current,
      effectiveUser?.entity?.id ? `kapp-${effectiveUser.entity.id}` : "",
      effectiveUser?.entity?.uid,
      effectiveUser?.uid,
    ].forEach((value) => {
      const normalized = normalizeIdentityValue(value);
      if (normalized) {
        uids.add(normalized);
      }
    });
    return uids;
  };

  const isCurrentUsersOutgoingMessage = (message, currentUid) => {
    const senderUid = normalizeIdentityValue(
      message?.sender?.uid
      || message?.data?.sender?.uid
      || message?.senderId
      || message?.sender?.guid,
    );
    const loggedInUids = getLoggedInCometUids(currentUid);

    if (senderUid && loggedInUids.has(senderUid)) {
      return true;
    }

    // Only fall back to name matching when CometChat did not provide a sender uid.
    if (!senderUid) {
      const senderName = normalizeIdentityValue(message?.sender?.name || message?.data?.sender?.name);
      const myNames = new Set();
      const displayName = `${effectiveUser?.entity?.first_name || ""} ${effectiveUser?.entity?.last_name || ""}`.trim();
      [
        displayName,
        effectiveUser?.entity?.first_name,
        effectiveUser?.entity?.username,
        effectiveUser?.username,
      ].forEach((value) => {
        const normalized = normalizeIdentityValue(value);
        if (normalized) {
          myNames.add(normalized);
        }
      });

      if (senderName && myNames.has(senderName)) {
        return true;
      }
    }

    return false;
  };

  const collectIdentityFromElement = (element, uids, names, includeTextContent = false) => {
    if (!element) {
      return;
    }

    const uidAttributes = [
      "data-uid",
      "data-user-id",
      "data-id",
      "data-conversation-id",
      "id",
    ];
    uidAttributes.forEach((attrName) => {
      const value = normalizeIdentityValue(element.getAttribute?.(attrName));
      if (value) {
        uids.add(value);
      }
    });

    const nameAttributes = ["data-name", "data-title", "aria-label", "title"];
    nameAttributes.forEach((attrName) => {
      const value = normalizeIdentityValue(element.getAttribute?.(attrName));
      if (value) {
        names.add(value);
      }
    });

    if (includeTextContent) {
      const textValue = normalizeIdentityValue(element.textContent);
      if (textValue) {
        names.add(textValue);
      }
    }
  };

  const collectActiveConversationIdentityFromRoot = (root) => {
    const uids = new Set();
    const names = new Set();
    if (!root || typeof root.querySelector !== "function") {
      return { uids, names };
    }

    const activeConversationNode = root.querySelector(
      ".cometchat-list-item--active, [aria-selected='true'], [aria-current='true'], .cometchat-message-header",
    );

    const conversationNameNode = root.querySelector(
      ".cometchat-message-header__name, .cometchat-message-header__title, .cometchat-list-item--active .cometchat-list-item__body-title, [aria-selected='true'] .cometchat-list-item__body-title, [aria-current='true'] .cometchat-list-item__body-title",
    );

    collectIdentityFromElement(activeConversationNode, uids, names, false);
    collectIdentityFromElement(conversationNameNode, uids, names, true);
    return { uids, names };
  };

  const getOpenConversationIdentity = () => {
    const allUids = new Set();
    const allNames = new Set();

    const mergeIdentity = (identity) => {
      identity.uids.forEach((value) => allUids.add(value));
      identity.names.forEach((value) => allNames.add(value));
    };

    mergeIdentity(collectActiveConversationIdentityFromRoot(document));

    const iframe = document.getElementById("cometChatMount")?.querySelector("iframe");
    if (iframe) {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          mergeIdentity(collectActiveConversationIdentityFromRoot(iframeDoc));
        }
      } catch (_err) {
        // Cross-origin iframe: cannot inspect active conversation.
      }
    }

    return {
      uids: allUids,
      names: allNames,
    };
  };

  const isIncomingMessageForOpenConversation = (message) => {
    if (!isOpenRef.current || !message?.sender) {
      return false;
    }

    const senderUid = normalizeIdentityValue(message?.sender?.uid);
    const senderName = normalizeIdentityValue(message?.sender?.name);
    if (!senderUid && !senderName) {
      return false;
    }

    const openConversation = getOpenConversationIdentity();
    if (!openConversation.uids.size && !openConversation.names.size) {
      return false;
    }

    const uidMatched = senderUid ? openConversation.uids.has(senderUid) : false;
    if (uidMatched) {
      return true;
    }

    return senderName ? openConversation.names.has(senderName) : false;
  };

  const rememberMessageForNotification = (message, currentUid) => {
    if (!message || isCurrentUsersOutgoingMessage(message, currentUid)) {
      return null;
    }

    const senderUid = String(message?.sender?.uid || "unknown");
    const bodyKey = normalizeNotificationBody(extractMessageText(message));
    const rawId = message?.id;
    const idValue = rawId == null ? "" : String(rawId);
    const syntheticId = idValue.startsWith("iframe-") || idValue.startsWith("iframe-poll-");
    const key = !idValue || syntheticId ? `body:${senderUid}:${bodyKey}` : `id:${idValue}`;

    if (shownNotificationKeysRef.current.has(key)) {
      return null;
    }

    return key;
  };

  const markNotificationShown = (key) => {
    if (!key) {
      return;
    }
    shownNotificationKeysRef.current.add(key);
    if (shownNotificationKeysRef.current.size > 150) {
      const overflow = shownNotificationKeysRef.current.size - 150;
      const firstKeys = Array.from(shownNotificationKeysRef.current).slice(0, overflow);
      firstKeys.forEach((k) => shownNotificationKeysRef.current.delete(k));
    }
    persistShownNotificationKeys();
  };

  const getNotificationIcon = (message) => {
    const avatar = message?.sender?.avatar;
    if (avatar && /^https?:\/\//i.test(String(avatar))) {
      return String(avatar);
    }
    try {
      return `${window.location.origin}/media/logos/khub-logo-light.ico`;
    } catch (_err) {
      return undefined;
    }
  };

  const displayWindowNotification = (title, options) => {
    const notification = new Notification(title, options);

    activeNotificationsRef.current.add(notification);

    notification.onclick = () => {
      window.focus();
      pendingFocusRef.current = {
        uid: options?.data?.senderUid || "",
        name: title,
        chatType: options?.data?.chatType || "user",
      };
      setIsOpen(true);
      notification.close();
    };
  
    return true;
  };

  const displayServiceWorkerNotification = async (title, options) => {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const reg = registrations?.find((item) => typeof item?.showNotification === "function");
    if (!reg) {
      return false;
    }

    await reg.showNotification(title, options);
    return true;
  };

  const showMessageNotification = async (message, currentUid) => {
    const dedupeKey = rememberMessageForNotification(message, currentUid);
    if (!dedupeKey) {
      return false;
    }
    if (document.visibilityState === "visible" && isIncomingMessageForOpenConversation(message)) {
      return false;
    }
    const senderName = extractSenderName(message);
    const extractedText = extractMessageText(message);
    const body = extractedText
      ? normalizeNotificationBody(extractedText)
      : "You received a new message";
    const looseKey = `loose:${normalizeIdentityValue(senderName)}:${body}`;
    if (shownNotificationKeysRef.current.has(looseKey)) {
      return false;
    }
    incrementCometChatUnreadCount();
    const receiverType = message?.receiverType || message?.receiver?.type;
    const isGroup = receiverType === "group";
    const senderUid = isGroup
      ? (message?.receiver?.guid || message?.receiverId || message?.sender?.uid || "")
      : (message?.sender?.uid || "");

    if (document.visibilityState === "visible") {
      pushInAppToast({
        senderName,
        senderUid,
        chatType: isGroup ? "group" : "user",
        body,
        avatar: getNotificationIcon(message),
      });
      markNotificationShown(dedupeKey);
      markNotificationShown(looseKey);
      return true;
    }

    if (typeof window === "undefined" || !("Notification" in window)) {
      return false;
    }
    if (!window.isSecureContext) {
      return false;
    }
    let permission = window.Notification.permission;
    if (permission === "default") {
      await requestNotificationPermission();
      permission = window.Notification.permission;
    }

    if (permission !== "granted") {
      return false;
    }
    const tag = `kapp-cometchat-${message?.id || Date.now()}`;
    const icon = getNotificationIcon(message);
    const notificationOptions = {
      body,
      icon,
      tag,
      data: { senderUid, chatType: isGroup ? "group" : "user" },
    };

    let shown = false;
    try {
      shown = displayWindowNotification(senderName, notificationOptions);
    } catch (windowErr) {
      console.warn("window.Notification failed, trying service worker:", windowErr);
      try {
        shown = await displayServiceWorkerNotification(senderName, notificationOptions);
      } catch (swErr) {
        console.warn("Service worker notification failed:", swErr);
      }
    }

    if (shown) {
      markNotificationShown(dedupeKey);
      markNotificationShown(looseKey);
    }

    return shown;
  };


  const getCometChatCalls = () =>
    window.CometChatCalls || window.CometChatApp?.CometChatCalls;

  const releaseEmbedCallSession = (sessionId) => {
    try {
      const iframe = document.getElementById("cometChatMount")?.querySelector("iframe");
      const win = iframe?.contentWindow;
      if (!win) {
        return;
      }
      const sid = sessionId || activeCallSessionIdRef.current;
      const innerChat = win.CometChat || win.CometChatApp?.CometChat;
      const innerCalls = win.CometChatCalls || win.CometChatApp?.CometChatCalls;

      if (innerChat && sid && typeof innerChat.endCall === "function") {
        Promise.resolve(innerChat.endCall(sid)).catch(() => {});
      }
      if (innerCalls?.endSession) {
        innerCalls.endSession();
      } else if (innerCalls?.CallSession?.getInstance) {
        const session = innerCalls.CallSession.getInstance();
        if (session) {
          if (typeof session.leaveSession === "function") {
            session.leaveSession();
          } else if (typeof session.endSession === "function") {
            session.endSession();
          }
        }
      }
      if (innerChat && typeof innerChat.clearActiveCall === "function") {
        innerChat.clearActiveCall();
      }
    } catch (err) {
      console.warn("CometChat embed iframe call release failed:", err);
    }
  };

  const getCallSessionId = (call) => {
    if (!call) {
      return activeCallSessionIdRef.current || "";
    }
    return (
      call.getSessionId?.()
      || call.getSessionID?.()
      || call.sessionId
      || call.sessionID
      || activeCallSessionIdRef.current
      || ""
    );
  };

  const clearCallMaxDurationTimer = () => {
    if (callMaxDurationTimerRef.current) {
      window.clearTimeout(callMaxDurationTimerRef.current);
      callMaxDurationTimerRef.current = null;
    }
  };

  const scheduleCallMaxDurationTimer = (call) => {
    clearCallMaxDurationTimer();
    const maxSec = Number(callMaxDurationSecRef.current) || 0;
    if (maxSec <= 0) {
      return;
    }
    callMaxDurationTimerRef.current = window.setTimeout(() => {
      callMaxDurationTimerRef.current = null;
      pushInAppToast({
        title: "Call ended",
        body: `Calls are limited to ${maxSec} seconds.`,
      });
      endCometChatCallLocally(call);
    }, maxSec * 1000);
  };

  const markCometChatCallUiActive = (call) => {
    const sessionId = getCallSessionId(call);
    if (sessionId) {
      activeCallSessionIdRef.current = String(sessionId);
    }
    setIsInCall(true);
  };

  const markCometChatCallConnected = (call) => {
    markCometChatCallUiActive(call);
    scheduleCallMaxDurationTimer(call);
  };

  const markCometChatCallInactive = () => {
    clearCallMaxDurationTimer();
    activeCallSessionIdRef.current = "";
    setIsInCall(false);
  };

  const releaseActiveCometChatCallSession = (sessionId) => {
    const CometChat = getCometChat();
    const calls = getCometChatCalls();

    try {
      if (calls?.endSession) {
        calls.endSession();
      } else if (calls?.CallSession?.getInstance) {
        const session = calls.CallSession.getInstance();
        if (session) {
          if (typeof session.leaveSession === "function") {
            session.leaveSession();
          } else if (typeof session.endSession === "function") {
            session.endSession();
          }
        }
      }
    } catch (err) {
      console.warn("CometChat call session release failed:", err);
    }

    try {
      if (CometChat && typeof CometChat.clearActiveCall === "function") {
        CometChat.clearActiveCall();
      }
    } catch (_err) {
      // ignore
    }

    releaseEmbedCallSession(sessionId);

    markCometChatCallInactive();
  };

  const endCometChatCallLocally = (call) => {
    const CometChat = getCometChat();
    const sessionId = getCallSessionId(call);
    if (CometChat && sessionId && typeof CometChat.endCall === "function") {
      Promise.resolve(CometChat.endCall(sessionId)).catch((err) => {
        console.warn("CometChat.endCall failed:", err);
      });
    }
    releaseActiveCometChatCallSession();
  };

  const removeCometChatCallListener = () => {
    if (callListenerRetryTimerRef.current) {
      window.clearInterval(callListenerRetryTimerRef.current);
      callListenerRetryTimerRef.current = null;
    }

    const CometChat = getCometChat();
    if (CometChat && typeof CometChat.removeCallListener === "function") {
      CometChat.removeCallListener(callListenerIdRef.current);
    }

    const calls = getCometChatCalls();
    if (calls && typeof calls.removeCallEventListener === "function") {
      calls.removeCallEventListener(callListenerIdRef.current);
    }
  };

  const registerCometChatCallListener = () => {
    const CometChat = getCometChat();
    if (
      !CometChat ||
      typeof CometChat.addCallListener !== "function" ||
      typeof CometChat.CallListener !== "function"
    ) {
      return false;
    }

    if (typeof CometChat.removeCallListener === "function") {
      CometChat.removeCallListener(callListenerIdRef.current);
    }

    const onRemoteCallClosed = (call) => {
      const sessionId = getCallSessionId(call);
      releaseActiveCometChatCallSession(sessionId);
    };

    CometChat.addCallListener(
      callListenerIdRef.current,
      new CometChat.CallListener({
        onIncomingCallReceived: (call) => {
          markCometChatCallUiActive(call);
        },
        onIncomingCallAccepted: (call) => {
          markCometChatCallConnected(call);
        },
        onOutgoingCallAccepted: (call) => {
          markCometChatCallConnected(call);
        },
        onIncomingCallCancelled: onRemoteCallClosed,
        onOutgoingCallRejected: onRemoteCallClosed,
        onCallEndedMessageReceived: onRemoteCallClosed,
      }),
    );

    const calls = getCometChatCalls();
    if (calls && typeof calls.addCallEventListener === "function") {
      if (typeof calls.removeCallEventListener === "function") {
        calls.removeCallEventListener(callListenerIdRef.current);
      }
      calls.addCallEventListener(callListenerIdRef.current, {
        onCallEnded: onRemoteCallClosed,
        onUserLeft: onRemoteCallClosed,
        onCallEndButtonPressed: () => {
          endCometChatCallLocally(null);
        },
      });
    }

    return true;
  };

  const ensureCometChatCallListener = () => {
    const registered = registerCometChatCallListener();
    if (registered) {
      return;
    }

    if (callListenerRetryTimerRef.current) {
      return;
    }

    let attempts = 0;
    callListenerRetryTimerRef.current = window.setInterval(() => {
      attempts += 1;
      const done = registerCometChatCallListener();
      if (done || attempts >= 10) {
        window.clearInterval(callListenerRetryTimerRef.current);
        callListenerRetryTimerRef.current = null;
      }
    }, 1500);
  };

  const removeCometChatMessageListener = () => {
    if (listenerRetryTimerRef.current) {
      window.clearInterval(listenerRetryTimerRef.current);
      listenerRetryTimerRef.current = null;
    }

    const CometChat = getCometChat();

    if (
      CometChat &&
      typeof CometChat.removeMessageListener === "function"
    ) {
      CometChat.removeMessageListener(messageListenerIdRef.current);
    }

    if (iframePollTimerRef.current) {
      window.clearInterval(iframePollTimerRef.current);
      iframePollTimerRef.current = null;
    }
    iframeLastSnapshotRef.current = null;

    injectTimeoutIdsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    injectTimeoutIdsRef.current = [];
  };

  const clearCometChatBrowserStorage = () => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && /cometchat|CC_|CometChat/i.test(key)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (_err) {}
  };

  const loginCometChatWithAuthToken = async (authToken) => {
    const CometChat = getCometChat();
    if (!CometChat || typeof CometChat.login !== "function" || !authToken) {
      return null;
    }
    try {
      return await CometChat.login(authToken);
    } catch (_firstErr) {
      try {
        if (typeof CometChat.logout === "function") {
          await CometChat.logout();
        }
      } catch (_logoutErr) {}
      clearCometChatBrowserStorage();
      return await CometChat.login(authToken);
    }
  };

  const destroyCometChatSession = async () => {
    clearCallMaxDurationTimer();
    removeCometChatCallListener();
    removeCometChatMessageListener();
    widgetStartPromiseRef.current = null;
    widgetStartConfigRef.current = null;
    widgetUidRef.current = "";
    loggedInCometUidRef.current = "";
    cometChatSessionRef.current = { appId: "", uid: "" };
    iframeLastSnapshotRef.current = null;
    const CometChat = getCometChat();
    if (CometChat && typeof CometChat.logout === "function") {
      try {
        await CometChat.logout();
      } catch (_err) {}
    }
    clearCometChatBrowserStorage();
    const mountEl = document.getElementById("cometChatMount");
    if (mountEl) mountEl.innerHTML = "";
    document.querySelectorAll('iframe[src*="cometchat"]').forEach((node) => {
      node.remove();
    });
  };

  const stopCometChatWidget = () => {
    // Keep iframe + polling alive while closed so incoming notifications still work.
  };

  const setupIframeFallbackWhenReady = (currentUid) => {
    const IFRAME_STYLE_ID = "kapp-cometchat-iframe-style";
    const IFRAME_CSS = `
      .cometchat-conversations__empty-state-view {
        position: relative;
      }
      .cometchat-conversations__empty-state-view::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: url("https://image2url.com/r2/default/files/1772135135161-0099bc7e-0518-4caa-8807-6370c624ada6.png") no-repeat center center !important;
        opacity: 0.1;
        pointer-events: none;
      }
      .cometchat-list__body {
        position: relative;
      }
      .cometchat-messages-wrapper .cometchat-message-list-wrapper .cometchat .cometchat-message-list__body.cometchat-message-list__body--spacing-top {
        position: relative;
      }
      .cometchat-messages-wrapper .cometchat-message-list-wrapper .cometchat .cometchat-message-list__body.cometchat-message-list__body--spacing-top::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background: url("https://image2url.com/r2/default/files/1772135135161-0099bc7e-0518-4caa-8807-6370c624ada6.png") no-repeat center center !important;
        opacity: 0.1;
        pointer-events: none;
      }
      .cometchat-list__empty-view {
        position: relative;
      }
      .cometchat-list__empty-view::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background: url("https://image2url.com/r2/default/files/1772135135161-0099bc7e-0518-4caa-8807-6370c624ada6.png") no-repeat center center !important;
        opacity: 0.1;
        pointer-events: none;
      }
      .cometchat-conversations-header__title {
        font-size: 0 !important;
        height: 30px;
      }
      .cometchat-conversations-header__title::before {
        content: "K-HUB CHAT";
        font-size: 22px !important;
      }
      .cometchat-conversations .cometchat-conversations-header {
        padding-right: 44px !important;
      }
      .cometchat-message-header {
        padding-right: 44px !important;
      }
    `;

    const tryAttach = () => {
      const mountEl = document.getElementById("cometChatMount");
      const iframe = mountEl?.querySelector("iframe");
      if (!iframe) {
        return false;
      }
      try {
        iframe.style.position = "absolute";
        iframe.style.inset = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "0";

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) {
          return false;
        }

        if (doc.documentElement) {
          doc.documentElement.style.overflowX = "hidden";
        }
        if (doc.body) {
          doc.body.style.overflowX = "hidden";
        }

        let style = doc.getElementById(IFRAME_STYLE_ID);
        if (!style) {
          style = doc.createElement("style");
          style.id = IFRAME_STYLE_ID;
          style.type = "text/css";
          style.appendChild(doc.createTextNode(IFRAME_CSS));
          doc.head?.appendChild(style);
        }

        setupIframePollingFallback(doc, currentUid);
        return true;
      } catch (err) {
        console.warn("Could not inject CSS into CometChat iframe (cross-origin or not ready):", err);
        return false;
      }
    };

    tryAttach();
    injectTimeoutIdsRef.current.push(window.setTimeout(tryAttach, 800));
    injectTimeoutIdsRef.current.push(window.setTimeout(tryAttach, 2000));
    injectTimeoutIdsRef.current.push(window.setTimeout(tryAttach, 5000));
  };

  const startCometChatWidgetIfNeeded = () => {
    const startCfg = widgetStartConfigRef.current;
    if (!startCfg || !window.CometChatApp?.CometChatAuth?.start) {
      return;
    }
    const mountEl = document.getElementById("cometChatMount");
    if (!mountEl) {
      return;
    }
    const cfgUid = startCfg.uid || startCfg.user?.uid || "";
    const existingIframe = mountEl.querySelector("iframe");
    if (existingIframe) {
      if (cfgUid && widgetUidRef.current && widgetUidRef.current !== cfgUid) {
        mountEl.innerHTML = "";
        widgetStartPromiseRef.current = null;
      } else {
        if (widgetUidRef.current) {
          setupIframeFallbackWhenReady(widgetUidRef.current);
        }
        return;
      }
    }
    if (widgetStartPromiseRef.current) {
      return;
    }
    const pending = pendingFocusRef.current;
    const realUid = pending?.uid && !String(pending.uid).startsWith("cometchat-fallback") ? pending.uid : "";
    const cfg = realUid
      ? { ...startCfg, chatType: pending.chatType || "user", defaultChatID: realUid }
      : startCfg;
    try {
      widgetStartPromiseRef.current = Promise.resolve(window.CometChatApp.CometChatAuth.start(cfg))
        .then(() => {
          if (widgetUidRef.current) setupIframeFallbackWhenReady(widgetUidRef.current);
          if (isMountedRef.current) setError(null);
        })
        .catch((err) => {
          widgetStartPromiseRef.current = null;
          console.error("CometChat widget failed to start:", err);
          if (isMountedRef.current) {
            setError(err?.message || "Chat widget failed to open. Please try again.");
          }
        });
    } catch (err) {
      widgetStartPromiseRef.current = null;
      console.error("CometChat widget failed to start:", err);
      if (isMountedRef.current) {
        setError(err?.message || "Chat widget failed to open. Please try again.");
      }
    }
  };

  const shouldBlockReadReceipts = () => {
    return !isOpenRef.current || document.visibilityState !== "visible";
  };

  const enqueuePendingReadReceiptCall = (methodName, args) => {
    if (!Array.isArray(args) || args.length === 0) {
      return;
    }

    pendingReadReceiptCallsRef.current.push({ methodName, args });
    if (pendingReadReceiptCallsRef.current.length > 200) {
      pendingReadReceiptCallsRef.current = pendingReadReceiptCallsRef.current.slice(-200);
    }
  };

  const flushPendingReadReceiptCalls = () => {
    if (shouldBlockReadReceipts()) {
      return;
    }

    const pending = pendingReadReceiptCallsRef.current;
    if (!pending.length) {
      return;
    }

    pendingReadReceiptCallsRef.current = [];
    pending.forEach((item) => {
      const originalFn = readReceiptOriginalFnsRef.current[item.methodName];
      if (typeof originalFn !== "function") {
        return;
      }

      Promise.resolve(originalFn(...item.args)).catch((err) => {
        console.warn(`CometChat.${item.methodName} replay failed:`, err);
      });
    });
  };

  const ensureReadReceiptGuard = (methodName) => {
    const CometChat = getCometChat();

    if (!CometChat || typeof CometChat[methodName] !== "function") {
        return false;
    }

    const currentFn = CometChat[methodName];
    if (currentFn.__kappReadReceiptGuarded) {
      return true;
    }

    const originalFn = currentFn.bind(CometChat);
    readReceiptOriginalFnsRef.current[methodName] = originalFn;
    const guardedFn = (...args) => {
      if (shouldBlockReadReceipts()) {
        enqueuePendingReadReceiptCall(methodName, args);
        return Promise.resolve();
      }
      return originalFn(...args);
    };

    guardedFn.__kappReadReceiptGuarded = true;
    CometChat[methodName] = guardedFn;
    return true;
  };

  const ensureMarkAsReadGuard = () => {
    const guardedRead = ensureReadReceiptGuard("markAsRead");
    const guardedDelivered = ensureReadReceiptGuard("markAsDelivered");
    return guardedRead || guardedDelivered;
  };

  const getIframeSenderDetails = (doc) => {
    if (!doc) {
      return { uid: "cometchat-fallback", name: "New message", avatar: undefined };
    }

    const name = (
      doc.querySelector(
        ".cometchat-message-header__name, .cometchat-message-header__title, .cometchat-list-item--active .cometchat-list-item__body-title, [aria-selected='true'] .cometchat-list-item__body-title, [aria-current='true'] .cometchat-list-item__body-title, .cometchat-list-item__body-title",
      )?.textContent || ""
    ).trim();
    const avatar = (doc.querySelector(".cometchat-list-item__leading-view img")?.getAttribute?.("src") || "").trim() || undefined;

    const safeName = name || "New message";
    const activeNode = doc.querySelector(".cometchat-list-item--active, [aria-selected='true'], [aria-current='true']");
    const attrUid = activeNode?.getAttribute?.("data-uid")
      || activeNode?.getAttribute?.("data-user-id")
      || activeNode?.getAttribute?.("data-id");
    const safeUid = attrUid
      ? String(attrUid)
      : `cometchat-fallback-${safeName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

    return {
      uid: safeUid,
      name: safeName,
      avatar,
    };
  };

  const setupIframePollingFallback = (doc, currentUid) => {
    if (iframePollTimerRef.current) {
      return;
    }

    const getLiveIframeDoc = () => {
      try {
        const iframe = document.getElementById("cometChatMount")?.querySelector("iframe");
        return iframe?.contentDocument || iframe?.contentWindow?.document || doc || null;
      } catch (_err) {
        return doc || null;
      }
    };

    const parseCount = (value) => {
      const parsed = Number.parseInt(String(value || "").trim(), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    };

    const getConversationRows = (root) => {
      const rows = Array.from(root.querySelectorAll(".cometchat-conversations .cometchat-list-item"));
      if (rows.length) {
        return rows;
      }
      return Array.from(root.querySelectorAll(".cometchat-list-item"));
    };

    const getConversationSnapshot = (root) => {
      const rows = getConversationRows(root);
      return rows.map((row, index) => {
        const title = (row.querySelector(".cometchat-list-item__body-title")?.textContent || "").trim();
        const subtitle = (
          row.querySelector(".cometchat-conversations__subtitle-text")?.textContent
          || row.querySelector(".cometchat-list-item__body-subtitle")?.textContent
          || ""
        ).trim();
        const unread = parseCount(
          row.querySelector(".cometchat-conversations__trailing-view-badge-count")?.textContent
          || row.querySelector("[class*='badge']")?.textContent
          || row.querySelector("[data-unread-count]")?.getAttribute?.("data-unread-count"),
        );
        const avatar = (
          row.querySelector(".cometchat-avatar__image")?.getAttribute?.("src")
          || row.querySelector(".cometchat-avatar img")?.getAttribute?.("src")
          || ""
        ).trim() || undefined;
        const key = row.getAttribute("id") || `${title}-${index}`;
        const uid = row.getAttribute("data-uid")
          || row.getAttribute("data-user-id")
          || row.getAttribute("data-id")
          || "";
        return { key, title, subtitle, unread, avatar, uid };
      });
    };

    const getIncomingConversationUpdate = (snapshot, prev) => {
      if (!prev?.conversations || prev.conversations.length === 0) {
        return null;
      }
      const previousRows = new Map(prev.conversations.map((item) => [item.key, item]));
      for (const row of snapshot.conversations || []) {
        const prevRow = previousRows.get(row.key);
        // Ignore "Typing..." changes entirely
        const currentSubtitleLower = String(row.subtitle || "").toLowerCase();
        if (currentSubtitleLower.includes("typing...")) {
          continue;
        }

        if (prevRow) {
          const unreadIncreased = row.unread > (prevRow.unread || 0);
          if (unreadIncreased) {
            return row;
          }
        } else if (row.unread > 0 && snapshot.unread > prev.unread) {
          return row;
        }
      }
      return null;
    };

    const getSnapshot = () => {
      const liveDoc = getLiveIframeDoc();
      if (!liveDoc?.body) {
        return null;
      }

      const pollRoot = liveDoc.querySelector(".cometchat-messages-wrapper") || liveDoc.body;
      const badges = Array.from(
        pollRoot.querySelectorAll("[class*='unread'], [class*='badge'], [data-unread-count]"),
      );

      const textNodes = pollRoot.querySelectorAll(".cometchat-message-bubble__text, [class*='message'][class*='text']");
      const preview = normalizeNotificationBody(textNodes[textNodes.length - 1]?.textContent || "").slice(0, 120);
      const messageNodes = pollRoot.querySelectorAll("[class*='message']").length;
      const conversations = getConversationSnapshot(liveDoc);
      const rowUnread = conversations.reduce((count, row) => count + row.unread, 0);
      const unread = conversations.length ? rowUnread : badges.reduce((count, node) => {
        return count + parseCount(node?.textContent);
      }, 0);
      return { unread, preview, messageNodes, conversations, hasRows: conversations.length > 0 };
    };

    iframePollTimerRef.current = window.setInterval(() => {
      try {
        const snapshot = getSnapshot();
        if (!snapshot) {
          return;
        }

        if (snapshot.hasRows) {
          publishCometChatUnreadCount(snapshot.unread);
        }

        const prev = iframeLastSnapshotRef.current;

        if (!prev) {
          iframeLastSnapshotRef.current = snapshot;
          return;
        }

        const unreadIncreased = snapshot.unread > prev.unread;
        const changedConversation = getIncomingConversationUpdate(snapshot, prev);
        const hasNewIncomingActivity = unreadIncreased || Boolean(changedConversation);

        if (hasNewIncomingActivity) {
          const now = Date.now();
          if (now - lastIframeNotificationAtRef.current > 5000) {
            const sender = changedConversation
              ? {
                uid: changedConversation.uid || `cometchat-fallback-${normalizeIdentityValue(changedConversation.key || changedConversation.title || "unknown")}`,
                name: changedConversation.title || "New message",
                avatar: changedConversation.avatar,
              }
              : getIframeSenderDetails(getLiveIframeDoc());
            const derivedPreview = changedConversation?.subtitle || snapshot.preview;
            
            showMessageNotification(
              {
                sender,
                type: "text",
                text: derivedPreview || "You have a new chat message",
                id: `iframe-poll-${changedConversation?.key || now}`,
              },
              currentUid,
            ).then((success) => {
              if (success) {
                lastIframeNotificationAtRef.current = now;
              }
            });
          }
        }

        iframeLastSnapshotRef.current = snapshot;
      } catch (err) {
        iframeLastSnapshotRef.current = null;
        console.warn("CometChat polling fallback tick failed; retrying next interval:", err);
      }
    }, 4000);
  };

  const registerCometChatMessageListener = (currentUid) => {
    const CometChat = getCometChat();

    if (
      !CometChat ||
      typeof CometChat.addMessageListener !== "function" ||
      typeof CometChat.MessageListener !== "function"
    ) {
      return false;
    }

    if (typeof CometChat.removeMessageListener === "function") {
      CometChat.removeMessageListener(messageListenerIdRef.current);
    }
  
    const messageListener = new CometChat.MessageListener({
      onTextMessageReceived: (message) => {
        if (isCurrentUsersOutgoingMessage(message, currentUid)) {
          return;
        }
  
        void showMessageNotification(message, currentUid);
      },
  
      onMediaMessageReceived: (message) => {
        if (isCurrentUsersOutgoingMessage(message, currentUid)) {
          return;
        }
  
        void showMessageNotification(message, currentUid);
      },
  
      onCustomMessageReceived: (message) => {
        if (isCurrentUsersOutgoingMessage(message, currentUid)) {
          return;
        }
  
        void showMessageNotification(message, currentUid);
      },
    });
  
    CometChat.addMessageListener(
      messageListenerIdRef.current,
      messageListener
    );
  
    return true;
  };

  const ensureCometChatMessageListener = (currentUid) => {
    const registered = registerCometChatMessageListener(currentUid);
    if (registered) {
      return;
    }

    if (listenerRetryTimerRef.current) {
      return;
    }

    let attempts = 0;
    listenerRetryTimerRef.current = window.setInterval(() => {
      attempts += 1;
      const done = registerCometChatMessageListener(currentUid);

      if (done || attempts >= 10) {
        window.clearInterval(listenerRetryTimerRef.current);
        listenerRetryTimerRef.current = null;
      }
    }, 1500);
  };

  const publishCometChatAccess = (hasAccess) => {
    setHasCometChatAccess(hasAccess);
    window.__kappCometChatAccess = hasAccess;
    if (!hasAccess) {
      clearCometChatUnreadCount();
    }
    window.dispatchEvent(new CustomEvent("kapp:cometchat-availability", { detail: { hasAccess } }));
  };

  const publishCometChatState = (open) => {
    window.__kappCometChatOpen = open;
    window.dispatchEvent(new CustomEvent("kapp:cometchat-state", { detail: { open } }));
  };

  const clickConversationInIframe = (name, uid) => {
    const iframe = document.getElementById("cometChatMount")?.querySelector("iframe");
    let doc = null;
    try {
      doc = iframe?.contentDocument || iframe?.contentWindow?.document || null;
    } catch (_err) {
      return false;
    }
    if (!doc) return false;
    const rows = Array.from(doc.querySelectorAll(".cometchat-list-item"));
    const targetName = normalizeIdentityValue(name);
    const targetUid = normalizeIdentityValue(uid);
    const row = rows.find((el) => {
      const attrs = ["data-uid", "data-user-id", "data-id", "id"].map((attr) =>
        normalizeIdentityValue(el.getAttribute?.(attr)),
      );
      if (targetUid && attrs.includes(targetUid)) return true;
      const title = normalizeIdentityValue(el.querySelector(".cometchat-list-item__body-title")?.textContent);
      return Boolean(targetName && title && title === targetName);
    });
    if (!row) return false;
    row.click();
    return true;
  };

  const tryOpenConversation = (target) => {
    if (!target) return false;
    const app = window.CometChatApp;
    const uid = String(target.uid || "");
    const realUid = uid && !uid.startsWith("cometchat-fallback") ? uid : "";
    const opener = target.chatType === "group" ? app?.chatWithGroup : app?.chatWithUser;
    if (realUid && typeof opener === "function" && document.getElementById("cometChatMount")?.querySelector("iframe")) {
      try {
        opener.call(app, realUid);
        return true;
      } catch (err) {
        console.warn("CometChat open conversation failed:", err);
      }
    }
    return clickConversationInIframe(target.name, realUid);
  };

  const focusPendingConversation = () => {
    const target = pendingFocusRef.current;
    if (!target) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (tryOpenConversation(target) || attempts >= 12) {
        window.clearInterval(timer);
        pendingFocusRef.current = null;
      }
    }, 350);
  };

  const openChatFromToast = (toast) => {
    dismissToast(toast.id);
    pendingFocusRef.current = {
      uid: toast.senderUid,
      name: toast.senderName,
      chatType: toast.chatType || "user",
    };
    setIsOpen(true);
    if (isOpenRef.current) focusPendingConversation();
  };

  useEffect(() => {
    const handleToggle = (event) => {
      const requestedOpen = event?.detail?.open;
      void requestNotificationPermission();
      setIsOpen((prev) => {
        const nextState = typeof requestedOpen === "boolean" ? requestedOpen : !prev;
        return nextState;
      });
    };

    window.addEventListener("kapp:toggle-cometchat", handleToggle);
    return () => {
      window.removeEventListener("kapp:toggle-cometchat", handleToggle);
    };
  }, []);

  useEffect(() => {
    publishCometChatState(isOpen);
    isOpenRef.current = isOpen;
    if (isOpen) {
      startCometChatWidgetIfNeeded();
      flushPendingReadReceiptCalls();
      focusPendingConversation();
    } else {
      stopCometChatWidget();
    }
  }, [isOpen]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(notificationStoreKeyRef.current);
      const parsed = raw ? JSON.parse(raw) : [];
      shownNotificationKeysRef.current = new Set(Array.isArray(parsed) ? parsed : []);
    } catch (_err) {
      shownNotificationKeysRef.current = new Set();
    }
  }, [storageScopeRef.current]);

  useEffect(() => {
    if (!hasCometChatAccess) {
      return;
    }

    const handleFirstInteraction = async () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx && !audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        if (audioCtxRef.current?.state === "suspended") void audioCtxRef.current.resume();
      } catch (_err) {}
      await requestNotificationPermission();

      const hasNotificationApi = typeof window !== "undefined" && "Notification" in window;
      const settledPermission = hasNotificationApi ? window.Notification.permission !== "default" : true;
      const shouldStopListening = !window.isSecureContext || !hasNotificationApi || settledPermission;

      if (shouldStopListening) {
        window.removeEventListener("click", handleFirstInteraction, true);
        window.removeEventListener("keydown", handleFirstInteraction, true);
      }
    };

    window.addEventListener("click", handleFirstInteraction, true);
    window.addEventListener("keydown", handleFirstInteraction, true);

    return () => {
      window.removeEventListener("click", handleFirstInteraction, true);
      window.removeEventListener("keydown", handleFirstInteraction, true);
    };
  }, [hasCometChatAccess]);

  useEffect(() => {
    if (!hasCometChatAccess) {
      return undefined;
    }

    ensureMarkAsReadGuard();

    const handleVisibilityChange = () => {
      ensureMarkAsReadGuard();
      flushPendingReadReceiptCalls();
      if (document.visibilityState === "visible" && isOpenRef.current) {
        clearCometChatUnreadCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const timer = window.setInterval(() => {
      // CometChat can rebind SDK methods after init/login; keep guards in place.
      ensureMarkAsReadGuard();
      flushPendingReadReceiptCalls();
    }, 1000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(timer);
    };
  }, [hasCometChatAccess]);

  // Try to obtain a bearer token from common storage or the user prop
  const getAuthToken = () => {
    return (
      effectiveUser?.entity?.auth_token || effectiveUser?.auth_token ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null
    );
  };

  const fetchCometChatConfig = async () => {
    try {
      const token = getAuthToken();
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // Use backend API endpoint for CometChat config
      const res = await fetch(`${API_BASE_URL}/core/cometchat/config`, {
        headers,
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) {
        // If 404 or other error, CometChat is not enabled - return empty
        return null;
      }
      const data = await res.json();
      // Handle response format (entity wrapper or direct)
      const config = data.entity || data;
      // Return null if config is empty (CometChat not enabled)
      if (!config || Object.keys(config).length === 0 || !config.appId) {
        return null;
      }
      return config;
    } catch (err) {
      // Silently fail - CometChat is not enabled
      return null;
    }
  };

  useEffect(() => {
    if (!effectiveUser?.entity?.id) {
      return undefined;
    }

    const initGeneration = initGenerationRef.current + 1;
    initGenerationRef.current = initGeneration;
    setError(null);
    setIsOpen(false);
    isOpenRef.current = false;
    publishCometChatState(false);
    setToasts([]);

    let uid = null;
    const chatHeaderText = "K-HUB CHAT";
    const emptyViewBackgroundImage = "https://image2url.com/r2/default/files/1772135135161-009";

    const initComet = async () => {
      await destroyCometChatSession();
      if (initGenerationRef.current !== initGeneration) {
        return;
      }

      const response = await fetchCometChatConfig();

      // If config not returned or empty, CometChat is not enabled - just return silently
      if (!response || !response.appId) {
        // CometChat is not enabled for this tenant - don't show error, just don't initialize
        publishCometChatAccess(false);
        setIsOpen(false);
        return;
      }

      const appId = response.appId || response.appID || response.app_id;
      const region = normalizeCometChatRegion(response.region);
      const authToken = response.authToken || response.auth_token || response.token;
      uid = response.uid || response.cometChatUid || `kapp-${effectiveUser.entity.id}`;
      const scope = getCometChatStorageScope(appId, uid);
      storageScopeRef.current = `${getTenantCookieScope()}:${scope}`;
      notificationStoreKeyRef.current = `kapp-cometchat-shown-notifications-${storageScopeRef.current}`;
      unreadStoreKeyRef.current = `kapp-cometchat-unread-count-${storageScopeRef.current}`;
      if (initGenerationRef.current !== initGeneration) {
        return;
      }
      cometChatSessionRef.current = { appId, uid };
      if (typeof window !== "undefined") {
        window.__kappCometChatCallRingTimeoutSec = Number(response.callRingTimeoutSeconds) || 30;
        const maxDuration = Number(response.callMaxDurationSeconds);
        callMaxDurationSecRef.current = Number.isFinite(maxDuration) ? maxDuration : 30;
        window.__kappCometChatCallMaxDurationSec = callMaxDurationSecRef.current;
      }
      
      if (!appId || !region) {
        // Config incomplete - silently don't show widget
        publishCometChatAccess(false);
        setIsOpen(false);
        return;
      }
      
      if (!authToken) {
        // Auth token not available - silently don't show widget
        publishCometChatAccess(false);
        setIsOpen(false);
        return;
      }

      publishCometChatAccess(true);

      try {
        await ensureCometChatEmbedScript();
      } catch (scriptErr) {
        console.error("CometChat embed script could not be loaded:", scriptErr);
        setError("Chat widget failed to load. Please refresh and try again.");
        return;
      }

      if (!isCometChatEmbedReady()) {
        console.error("CometChat embed script loaded but SDK globals are unavailable.");
        setError("Chat widget failed to initialize. Please refresh and try again.");
        return;
      }

      const mountConfig = {
        mount: "#cometChatMount",
        width: "400px",
        height: "100%",
        isDocked: false,
        // dockedAlignment: "right",
        uid,
        user: { name: effectiveUser.entity.first_name || effectiveUser.entity.username },
        overrideStyles: `
          #cometChatMount {
            bottom: 20px;
            right: 20px;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          }

          .cometchat-list__empty-view::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 100%;
            height: 100%;
            background: url("${emptyViewBackgroundImage}") center/contain no-repeat;
            opacity: 0.1;
            pointer-events: none;
          }

          .cometchat-conversations-header__title {
            font-size: 0 !important;
            height: 30px;
          }

          .cometchat-conversations-header__title::before {
            content: "${chatHeaderText}";
            font-size: 22px !important;
            font-weight: 700;
            letter-spacing: 0.3px;
          }
        `,
      };

      try {
        const CometChat = getCometChat();
        // Preferred: use the CometChat SDK if available
        if (CometChat && typeof CometChat.init === "function") {
          await initCometChatSdk(appId, region);
          ensureMarkAsReadGuard();
          if (authToken && typeof CometChat.login === "function") {
            try {
              const loggedInUser = await loginCometChatWithAuthToken(authToken);
              loggedInCometUidRef.current = String(loggedInUser?.uid || "");
            } catch (loginErr) {
              console.error("CometChat.login failed:", loginErr);
              if (initGenerationRef.current === initGeneration && isMountedRef.current) {
                setError("Could not sign in to chat. Click Retry or refresh the page.");
                setIsOpen(true);
                isOpenRef.current = true;
                publishCometChatState(true);
              }
              return;
            }
          }
          if (initGenerationRef.current !== initGeneration) {
            return;
          }

          ensureCometChatMessageListener(uid);
          ensureCometChatCallListener();
          widgetUidRef.current = uid;

          if (window.CometChatApp && window.CometChatApp.CometChatAuth?.start) {
            const startCfg = {
              appID: appId,
              region,
              mode: "authToken",
              authToken,
              storageMode: "SESSION",
              ...mountConfig,
            };
            widgetStartConfigRef.current = startCfg;
            // Start once even when closed so background listeners/polling remain available.
            startCometChatWidgetIfNeeded();
            if (isOpenRef.current) {
              startCometChatWidgetIfNeeded();
            }
          }

          return;
        }

        // Fallback: use existing CometChatApp start method if SDK isn't present
        if (window.CometChatApp && window.CometChatApp.CometChatAuth?.start) {
          const cfg = {
            appID: appId,
            region,
            mode: "authToken",
            authToken: authToken,
            storageMode: "SESSION",
            uid,
            user: { name: effectiveUser.entity.first_name || effectiveUser.entity.username },
            ...mountConfig,
          };
          widgetStartConfigRef.current = cfg;
          widgetUidRef.current = uid;
          // Start once even when closed so background listeners/polling remain available.
          startCometChatWidgetIfNeeded();
          if (isOpenRef.current) {
            startCometChatWidgetIfNeeded();
          }
          ensureCometChatMessageListener(uid);
          ensureCometChatCallListener();
        }
      } catch (err) {
        console.error("CometChat init failed:", err);
        setError(err.message || String(err));
      }
    };

    initComet();

    return () => {
      initGenerationRef.current += 1;
      void destroyCometChatSession();
    };
  }, [effectiveUser?.entity?.id, retryKey]);

  if (!effectiveUser?.entity?.id) {
    return null;
  }

  return (
    <>
      {error && !isOpen && (
        <div className="cometchat-session-error" role="alert">
          {error}
          <button
            type="button"
            onClick={() => {
              setError(null);
              widgetStartPromiseRef.current = null;
              setRetryKey((n) => n + 1);
            }}
          >
            Retry
          </button>
        </div>
      )}
      {toasts.length > 0 && (
        <div className="cometchat-toast-stack">
          {toasts.map((toast) => (
            <button
              key={toast.id}
              type="button"
              className="cometchat-toast"
              onClick={() => openChatFromToast(toast)}
            >
              {toast.avatar ? (
                <img className="cometchat-toast__avatar" alt="" src={toast.avatar} />
              ) : (
                <span className="cometchat-toast__avatar" />
              )}
              <span className="cometchat-toast__body">
                <span className="cometchat-toast__name">{getToastHeadline(toast)}</span>
                {toast.body ? (
                  <span className="cometchat-toast__preview">{toast.body}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      )}
      <div
        className={`comet-chat-wrapper ${isOpen ? "is-open" : "is-closed"} ${isInCall ? "is-in-call" : ""}`}
        aria-hidden={isOpen ? undefined : true}
      >
        <button
          type="button"
          aria-label="Close chat"
          onClick={() => setIsOpen(false)}
          className="comet-chat-close-btn"
          tabIndex={isOpen ? 0 : -1}
          hidden={!isOpen}
        >
          <span aria-hidden="true">&minus;</span>
        </button>
        {error && (
          <div className="comet-chat-error">
            {error}
            <button type="button" onClick={() => { setError(null); widgetStartPromiseRef.current = null; setRetryKey((n) => n + 1); }}>
              Retry
            </button>
          </div>
        )}
        <div
          id="cometChatMount"
          className="comet-chat-mount"
        />
      </div>
    </>
  );
};

export default CometChatEmbed;